import { ToolType, type Tool } from "@repo/app-types/entities/tool";
import type {
  ToolProposalData,
  UpdateToolProposalData,
} from "@repo/app-types/entities/toolProposal";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { linkStyle } from "@repo/ui/Link/index";
import { IconSparkles } from "@tabler/icons-react";
import isEqual from "lodash-es/isEqual.js";
import type { FC } from "react";
import { twMerge } from "tailwind-merge";
import { listToolProposalFields } from "./toolProposalCardContent.js";

interface ToolProposalBannerProps {
  data: ToolProposalData;
  /** The tool as saved now; `undefined` for a new tool, which has no diffs. */
  tool?: Tool;
  /** Whether the form's text fields show their diffs. */
  isShowingChanges?: boolean;
  onToggleChanges?: () => void;
}

/** Whether a field the proposal changes was saved as something else since. */
function checkBaselineStale(data: UpdateToolProposalData, tool: Tool): boolean {
  const { baseline } = data;
  if (!baseline) {
    return false;
  }
  const savedSettings: Readonly<Record<string, unknown>> = tool.settings;
  const baselineSettings: Readonly<Record<string, unknown>> =
    baseline.settings ?? {};
  return (
    (baseline.name !== undefined && baseline.name !== tool.name) ||
    (baseline.description !== undefined &&
      baseline.description !== tool.description) ||
    Object.entries(baselineSettings).some(
      ([key, value]) => !isEqual(value, savedSettings[key]),
    )
  );
}

/**
 * The Ask Alpharun proposal in the tool's unsaved changes: what it changes,
 * the diffs on request, and what the user must do before it works. The text
 * fields show their diffs in the form, and the endpoint's shows here. Save
 * and discard are the form's own. For a new tool, it shows only what the tool
 * is for and what the user must do.
 */
export const ToolProposalBanner: FC<ToolProposalBannerProps> = (props) => {
  const { data, tool, isShowingChanges = false, onToggleChanges } = props;

  const change = data.kind === "update_tool" && tool ? data : undefined;
  const fields = change ? listToolProposalFields(change) : [];
  const notes = data.notes ?? [];
  const isStale = change && tool ? checkBaselineStale(change, tool) : false;
  const urlBaseline =
    change?.tool_type === ToolType.CUSTOM
      ? change.baseline?.settings?.url
      : undefined;
  const proposedUrl =
    change?.tool_type === ToolType.CUSTOM ? change.settings?.url : undefined;
  const hasUrlChange = urlBaseline !== undefined && proposedUrl !== undefined;
  const hasDiff =
    change !== undefined &&
    (change.name !== undefined ||
      change.description !== undefined ||
      (change.tool_type === ToolType.CODE &&
        change.settings?.code !== undefined) ||
      hasUrlChange);

  return (
    <div className="flex flex-col gap-2 rounded-lg bg-indigo-50 px-3 py-2 text-sm text-indigo-900">
      <div className="flex items-start gap-2">
        <IconSparkles
          aria-hidden
          className="mt-0.5 size-4 shrink-0 text-indigo-500"
        />
        <div className="min-w-0 flex-1">
          <p>{data.summary}</p>
          {data.kind === "create_tool" ? (
            <p className="mt-0.5 text-xs text-indigo-700">
              Suggested by Ask Alpharun. Review it, then create the tool.
            </p>
          ) : fields.length > 0 ? (
            <p className="mt-0.5 text-xs text-indigo-700">
              Suggested by Ask Alpharun: {fields.join(", ")}. Save to apply
              them.
            </p>
          ) : null}
        </div>
        {hasDiff && onToggleChanges ? (
          <Button
            variant={ButtonVariant.Tertiary}
            size={Size.SMALL}
            className={twMerge("shrink-0 cursor-pointer", linkStyle())}
            onClick={onToggleChanges}
          >
            {isShowingChanges ? "Hide changes" : "Show changes"}
          </Button>
        ) : null}
      </div>

      {isStale ? (
        <p className="rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-amber-800">
          The saved tool has changed since this suggestion was made. Saving
          replaces those changes with the suggestion.
        </p>
      ) : null}

      {notes.length > 0 ? (
        <ul className="list-disc rounded-md border border-amber-400 bg-amber-50 py-2 pr-3 pl-7 text-amber-800">
          {notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}

      {isShowingChanges && hasUrlChange ? (
        <p className="break-all">
          <span className="font-medium">API endpoint:</span>{" "}
          <span className="line-through">{urlBaseline}</span> → {proposedUrl}
        </p>
      ) : null}
    </div>
  );
};
